import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  CSSProperties,
  useCallback,
} from 'react';
import {
  Button,
  Col,
  Empty,
  Row,
  Select,
  Spin,
  Tooltip as TooltipAntd,
} from 'antd';
import { Column, ColumnConfig } from '@ant-design/plots';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import i18next from 'i18next';
import { QuestionCircleOutlined } from '@ant-design/icons';
import useSearchParams from '@hooks/useSearchParams';
import { useGraphColors } from '@hooks/useGraphColors';
import StatusMenu from '@components/StatusMenu';
import { StatusMenuContainer } from '@components/Table/styles';
import * as S from './styles';

interface DataPoint {
  type: string;
  date: string;
  total: number;
}

interface StatusCardItem {
  label: string;
  value: string;
  total: number;
  color: string;
}

interface ITags {
  status: string;
  product: string;
}

type ICharValues = [timestamp: string, total: number | null][];

interface ICharDataResponse {
  results?: {
    series?: {
      name: string;
      tags: ITags;
      columns: string[];
      values: ICharValues;
    }[];
    error: null | string;
  }[];
  error: null | string;
}

interface IStyleCss {
  [key: string]: CSSProperties;
}

const MIN_VALUE = 0;
const DEFAULT_DATE = 7;

const MainDashboard = () => {
  const styles: IStyleCss = {
    labelSelect: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    spinnerContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
    },
    spinner: {
      width: '100%',
      padding: '5rem 1rem',
      margin: 16,
    },
    cardContainer: { height: '100%' },
    paragraph: { margin: 0 },
    select: { width: '15rem' },
    rowCard: {
      height: '50%',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    labelCard: { fontSize: '25px', lineHeight: '1.2' },
    valueCard: {
      textAlign: 'end',
      fontSize: '36px',
      fontWeight: '500',
      lineHeight: '1.2',
    },
    notFoundMessage: {
      textAlign: 'center',
    },
    questionIcon: {
      marginLeft: '8px',
    },
  };

  const { setSearchParams, clearSearchParams, searchParams } = useSearchParams({
    extraParams: ['lastTime', 'groupByTime'],
  });

  const [selectedDate, setSelectedDate] = useState<number>(
    Number(searchParams?.lastTime) || DEFAULT_DATE,
  );
  const { customersSwitch, theme, graphThemeKey } = useContext(SessionContext);
  const [data, setData] = useState<DataPoint[]>([]);
  const [statusCardItems, setStatusCardItems] = useState<StatusCardItem[]>([]);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const graphColors = useGraphColors();

  const { t } = useTranslation(['date-picker', 'main-dashboard']);

  const translateStatus = useCallback(
    (status: string): string => {
      const translated = t(`main-dashboard:${status?.toUpperCase()}`);
      return translated === `main-dashboard:${status?.toUpperCase()}`
        ? status
        : translated;
    },
    [t],
  );

  const fetchParams = useMemo(
    () => ({
      params: {
        lastTime: `${selectedDate}d`,
        groupByTime: '1d',
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [customersSwitch?.selected, selectedDate],
  );

  const { data: chartData, isLoading: charIsLoading } =
    useSwr<ICharDataResponse>(
      '/service-proxy/dashboard/simcard_ativacao/grafico',
      fetchParams,
    );

  const formatDateFromISO = useCallback(
    (isoString: string) => {
      if (
        !isoString ||
        typeof isoString !== 'string' ||
        isoString.length < 10
      ) {
        return '';
      }

      const day = isoString.substring(8, 10);
      const month = isoString.substring(5, 7);

      return currentLanguage === 'en' ? `${month}/${day}` : `${day}/${month}`;
    },
    [currentLanguage],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!chartData?.results?.[0]?.series) return;

        const formattedData: DataPoint[] = [];

        const uniqueDates = new Set<string>();

        chartData.results[0].series.forEach(series => {
          series.values.forEach(([timestamp]) => {
            if (timestamp) {
              uniqueDates.add(timestamp);
            }
          });
        });

        const sortedDates = Array.from(uniqueDates).sort();

        const statusDateMap = new Map<string, Map<string, number>>();

        chartData.results[0].series.forEach(series => {
          const { status } = series.tags;
          const translatedStatus = translateStatus(status) || 'Unknown';

          if (!statusDateMap.has(translatedStatus)) {
            statusDateMap.set(translatedStatus, new Map<string, number>());
          }

          const dateMap = statusDateMap.get(translatedStatus) as Map<
            string,
            number
          >;

          series.values.forEach(([timestamp, total]) => {
            if (timestamp && total !== null && total !== undefined) {
              const currentTotal = dateMap.get(timestamp) || 0;
              dateMap.set(timestamp, currentTotal + total);
            }
          });
        });

        statusDateMap.forEach((dateMap, translatedStatus) => {
          sortedDates.forEach(date => {
            const total = dateMap.get(date) || MIN_VALUE;
            formattedData.push({
              type: translatedStatus,
              date,
              total: Math.round(total),
            });
          });
        });

        setData(formattedData);

        const statusTotals = new Map<string, number>();

        chartData.results[0].series.forEach(series => {
          const { status } = series.tags;
          const lastValue = series.values[series.values.length - 1];

          if (lastValue && lastValue[1] !== null) {
            const currentTotal = statusTotals.get(status) || 0;
            statusTotals.set(status, currentTotal + lastValue[1]);
          }
        });

        const statusItems: StatusCardItem[] = Array.from(
          statusTotals.entries(),
        ).map(([status, total]) => ({
          label: translateStatus(status),
          value: status.toLowerCase(),
          total: Math.round(total),
          color: 'var(--primary-color)',
        }));

        setStatusCardItems(statusItems);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    fetchData();
    i18next.on('languageChanged', fetchData);

    return () => {
      i18next.off('languageChanged', fetchData);
    };
  }, [chartData, translateStatus]);

  const formatValue = (value: number): string =>
    value >= 1000
      ? `${Math.round(value / 1000)}K`
      : Math.round(value).toString();

  const options = useMemo(
    () => [
      { label: t('last-week', { days: 7 }), value: 7 },
      { label: t('last-two-weeks', { days: 14 }), value: 14 },
      { label: t('last-three-weeks', { days: 21 }), value: 21 },
      { label: t('last-month', { days: 30 }), value: 30 },
      { label: t('last-two-months', { days: 60 }), value: 60 },
      { label: t('last-three-months', { days: 90 }), value: 90 },
    ],
    [t],
  );

  const { isLoadingData, hasData } = useMemo(
    () => ({
      isLoadingData: charIsLoading,
      hasData: !!data?.length,
    }),
    [charIsLoading, data],
  );

  const getSelectValue = (searchParam: number, defaultValue: number) => {
    const isValidValue = options?.find(option => option?.value === searchParam);

    if (!isValidValue && !!searchParam) {
      clearSearchParams();
      return defaultValue;
    }

    return isValidValue ? searchParam : defaultValue;
  };

  const config = useMemo<ColumnConfig>(() => {
    return {
      data,
      xField: 'date',
      yField: 'total',
      seriesField: 'type',
      isStack: true,
      scrollbar: {
        type: 'horizontal',
        height: 14,
        style: {
          trackColor: theme?.colors?.content1,
          thumbColor: theme?.colors?.background1,
          thumbHighlightColor: theme?.colors?.background1,
        },
      },
      color: graphColors[graphThemeKey],
      xAxis: {
        label: {
          autoRotate: true,
          formatter: (val: string) => formatDateFromISO(val),
          style: {
            fill: theme?.colors?.text,
          },
        },
      },
      yAxis: {
        label: {
          type: 'log',
          formatter: (val: string) => formatValue(Number(val)),
          style: {
            fill: theme?.colors?.text,
          },
        },
      },
      legend: {
        position: 'bottom' as const,
        layout: 'horizontal' as const,
        itemName: {
          style: {
            fill: theme?.colors?.text,
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: (datum: any) => {
          return {
            name: datum.type,
            value: Math.round(datum.total).toString(),
            title: formatDateFromISO(datum.date),
          };
        },
      },
      interactions: [
        {
          type: 'element-active',
        },
        {
          type: 'legend-filter',
          enable: true,
        },
      ],
      animation: false,
    };
  }, [
    data,
    theme?.colors?.background1,
    theme?.colors?.content1,
    theme?.colors?.text,
    graphColors,
    graphThemeKey,
    formatDateFromISO,
  ]);

  return (
    <S.DashContainer>
      <Row gutter={[16, 16]}>
        <Col xl={24} xs={24}>
          <S.CardContainer
            title={
              <div className="card-title">
                <Row align="middle">
                  <div>{t('main-dashboard:titleCard')}</div>
                  <TooltipAntd title={t('main-dashboard:tooltipMessage')}>
                    <Button
                      type="link"
                      icon={
                        <QuestionCircleOutlined style={styles.questionIcon} />
                      }
                    />
                  </TooltipAntd>
                </Row>
                <div>
                  <Select
                    onChange={lastTime => {
                      setSelectedDate(lastTime);
                      setSearchParams({
                        lastTime,
                      });
                    }}
                    value={getSelectValue(
                      Number(searchParams?.lastTime),
                      selectedDate,
                    )}
                    options={options}
                    style={styles.select}
                    className="card-select-filter"
                  />
                </div>
              </div>
            }
          >
            {(() => {
              if (isLoadingData) {
                return (
                  <div style={styles.spinnerContainer}>
                    <Spin style={styles.spinner} />
                  </div>
                );
              }

              if (hasData) {
                return (
                  <S.BaseEvolutionMenuCardContainer className="base-evolution-status-menu-container">
                    <StatusMenuContainer>
                      <StatusMenu
                        data={statusCardItems}
                        handleSelectedCard={() => {
                          // Sem operação: Intencionalmente vazio para desabilitar o comportamento de seleção do card
                        }}
                      />
                    </StatusMenuContainer>
                    <Column {...config} height={400} />
                  </S.BaseEvolutionMenuCardContainer>
                );
              }

              return (
                <Empty
                  description={t('main-dashboard:notFoundMessage')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              );
            })()}
          </S.CardContainer>
        </Col>
      </Row>
    </S.DashContainer>
  );
};

export default MainDashboard;
